import { Box, Text, VStack, useColorModeValue } from '@chakra-ui/react';
import type { Country } from '@/models/country';
import type { Fixture } from '@/models/fixture';
import type { gameResult } from '@/models/gameUpdate';
import { findGameResultForFixture, partitionTeamFixtures, sumTournamentPointsForTeam } from './teamDetailUtils';
import { useTeamDetail } from './TeamDetailContext';
import { TeamDetailSectionCard } from './TeamDetailSectionCard';
import { TeamDetailFixtureRow } from './TeamDetailFixtureRow';

function FixtureList(props: { fixtures: Fixture[]; country: Country; gameResults: gameResult[]; showResults?: boolean }) {
  if (props.fixtures.length === 0) {
    return (
      <Text fontSize='sm' color='gray.500'>
        None
      </Text>
    );
  }
  return (
    <VStack align='stretch' spacing={2}>
      {props.fixtures.map((f) => (
        <TeamDetailFixtureRow
          key={f.matchId ?? `${f.sortOrder}-${f.date.toMillis()}`}
          fixture={f}
          focusCountry={props.country}
          gameResult={props.showResults ? findGameResultForFixture(f, props.gameResults) : undefined}
        />
      ))}
    </VStack>
  );
}

export function TeamDetailFixtures(props: {
  country: Country;
  fixtures: Fixture[];
  gameResults: gameResult[];
}) {
  const { progressionAwards } = useTeamDetail();
  const { upcoming, played } = partitionTeamFixtures(props.fixtures, props.gameResults, props.country);
  const hasAny = upcoming.length > 0 || played.length > 0;
  const tournamentPoints = sumTournamentPointsForTeam(
    props.gameResults,
    progressionAwards,
    props.country,
  );
  const pointsColor = useColorModeValue('gray.700', 'gray.200');
  const pointsBoxBg = useColorModeValue('whiteAlpha.700', 'whiteAlpha.100');
  const pointsBoxBorder = useColorModeValue('blackAlpha.100', 'whiteAlpha.200');

  return (
    <Box w='full' mt={-2}>
      <Box ml='auto' mb={1.5} w='fit-content'>
        <Box
          px={2.5}
          py={1}
          borderRadius='md'
          borderWidth='1px'
          borderColor={pointsBoxBorder}
          bg={pointsBoxBg}
        >
          <Text fontSize='xs' fontWeight='semibold' color={pointsColor} whiteSpace='nowrap' lineHeight='short'>
            {tournamentPoints} {tournamentPoints === 1 ? 'point' : 'points'}
          </Text>
        </Box>
      </Box>
      <TeamDetailSectionCard title='Fixtures'>
      {!hasAny ? (
        <Text fontSize='sm' color='gray.500'>
          No fixtures found for this team.
        </Text>
      ) : (
        <VStack align='stretch' spacing={4}>
          <VStack align='stretch' spacing={2}>
            <Text fontSize='xs' fontWeight='semibold' textTransform='uppercase' letterSpacing='wider' color='gray.500'>
              Upcoming
            </Text>
            <FixtureList fixtures={upcoming} country={props.country} gameResults={props.gameResults} />
          </VStack>
          <VStack align='stretch' spacing={2}>
            <Text fontSize='xs' fontWeight='semibold' textTransform='uppercase' letterSpacing='wider' color='gray.500'>
              Played / past
            </Text>
            <FixtureList fixtures={played} country={props.country} gameResults={props.gameResults} showResults />
          </VStack>
        </VStack>
      )}
      </TeamDetailSectionCard>
    </Box>
  );
}
